import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultGratOverviewPageOneData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'grat-overview-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: 'What is a GRAT?',
                variant: 'h3',
              },
            },
            {
              id: 'grat-overview-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: 'A Grantor Retained Annuity Trust (GRAT) is a wealth transfer technique used to transfer assets outside of the grantor\'s estate while using little to no gift tax exemption. GRATs are typically used to "freeze" the size of an estate by transferring appreciation of assets out of the grantor\'s taxable estate during the lifetime of the grantor, with any appreciation being free of gift and estate tax.\n\nThe grantor funds the GRAT with a gift. During the trust term, the GRAT pays the grantor annuity payments which are equivalent to the funding amount and the applicable interest rate set by the IRS (7520 rate). When the term ends, any appreciation beyond the funding amount plus interest passes to the beneficiaries. **Assets with high growth potential are common candidates for GRATs.**',
              },
            },
          ],
        },
      },
      {
        id: 'benefits',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'grat-overview-page-one-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 2,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'grat-overview-benefits-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential benefits',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'grat-benefit-estate-tax',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Estate tax reduction',
                      description:
                        'GRATs move asset appreciation and future income out of the estate, which can significantly reduce future estate tax liability.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'grat-benefit-gift-tax',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Gift tax-free transfer',
                      description:
                        'If a GRAT pays back the full principal and interest based on the applicable IRS interest rate (known as the 7520 rate), it can pass assets to beneficiaries without requiring a taxable gift.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'grat-benefit-tax-free-growth',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Income tax-free growth',
                      description:
                        'Taxes generated by the trust are paid by the grantor using assets outside of the trust, further reducing potential estate tax exposure. Annuity payments made to the grantor are not taxable.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'grat-benefit-limited-downside',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Limited downside',
                      description:
                        'If a GRAT outperforms the 7520 rate, wealth is transferred to beneficiaries. If a GRAT underperforms the rate, the assets are returned to the grantor through annuity payments, and the only downside is the upfront cost of setting up the GRAT and the lack of access to funds during the GRAT term.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'grat-overview-drawbacks-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential drawbacks',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'grat-drawback-lifetime',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Grantor lifetime considerations',
                      description:
                        "If the grantor passes away during the trust term, assets held in the GRAT are returned to the grantor's estate and may be subject to estate tax.",
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'grat-drawback-gst',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Inter-generational (G3) transfer',
                      description:
                        'GRAT remainder trusts cannot be Generation Skipping Transfer (GST) tax exempt. The GRAT remainder may be subject to GST tax if it benefits the 3rd generation.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'grat-drawback-valuations',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Private asset utilization',
                      description:
                        'Given that annuity payments are made annually, the requirement for annual asset valuations can be challenging if the GRAT contains an infrequently valued asset (i.e. privately-held investments).',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'grat-drawback-complexity',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Complexity & costs',
                      description:
                        'Creating and maintaining a GRAT involves legal and administrative expenses, and the structure may require ongoing professional management.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
